import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { API_BASE_URL } from '../config';
import { authFetch } from '../api/config';
import { getAuthHeaders } from '../api/workosAuth';
import { decodeUnicodeEscapes } from '../utils/decodeUnicode';
import { usePostHog } from '@posthog/react';
import { useAuth } from './AuthContext';

const CURRENT_PROJECT_STORAGE_KEY = 'currentProjectId';

/** UUID / id in URL for routes that encode the active project (deep links). */
function routeProjectIdFromPath(pathname: string): string | null {
  if (!pathname || pathname === '/') return null;
  const patterns = [
    /^\/app\/projects\/([^/]+)/,
    /^\/app\/dashboard\/([^/]+)/,
    /^\/app\/project-state\/([^/]+)(?:\/|$)/,
    /^\/app\/xer\/project\/([^/]+)(?:\/|$)/,
    /^\/app\/legacy\/projects\/([^/]+)(?:\/|$)/,
  ];
  for (const re of patterns) {
    const m = pathname.match(re);
    if (m?.[1]) return decodeURIComponent(m[1]);
  }
  return null;
}

interface Project {
  id: string;
  name: string;
  organization?: string;
  description?: string;
  status?: string;
  created_at: string;
  has_xer_data?: boolean;
  display_name?: string;
  filename?: string;
  uploaded_at?: string;
  xer_data?: {
    xer_project: any;
    import_info: {
      file_name: string;
      imported_at: string;
      import_run_id: string;
    };
  };
}

export interface BaselineSnapshot {
  id: string;
  snapshot_name?: string;
  file_name?: string;
  uploaded_at?: string;
  is_baseline?: boolean;
}

/** Snapshot option for context selector (all snapshots for project). */
export interface SnapshotOption {
  id: string;
  snapshot_name: string;
  uploaded_at: string | null;
  is_baseline?: boolean;
  is_latest?: boolean;
  import_run_id?: string;
  total_activities?: number;
}

interface GlobalProjectContextType {
  // Project Management
  selectedProject: Project | null;
  projects: Project[];
  setSelectedProject: (project: Project | null) => void;

  // Context: snapshots for selected project (default to Latest)
  snapshots: SnapshotOption[];
  snapshotsLoading: boolean;
  selectedSnapshotId: string | null;
  setSelectedSnapshotId: (id: string | null) => void;
  refreshSnapshots: () => Promise<void>;

  // Baselines for selected project (named baselines; user can select one)
  baselines: BaselineSnapshot[];
  baselinesLoading: boolean;
  selectedBaselineId: string | null;
  setSelectedBaselineId: (id: string | null) => void | Promise<void>;
  refreshBaselines: () => Promise<void>;

  // Loading and Error States
  projectsLoading: boolean;
  projectsError: string | null;

  // Methods
  /** After creating a project, pass its id so global selection switches to it (not the previous selection). */
  refreshProjects: (preferProjectId?: string) => Promise<void>;
  selectProjectById: (projectId: string) => void;
}

const GlobalProjectContext = createContext<GlobalProjectContextType | undefined>(undefined);

interface GlobalProjectProviderProps {
  children: ReactNode;
}

export const GlobalProjectProvider: React.FC<GlobalProjectProviderProps> = ({ children }) => {
  const posthog = usePostHog();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotOption[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [baselines, setBaselines] = useState<BaselineSnapshot[]>([]);
  const [baselinesLoading, setBaselinesLoading] = useState(false);
  const [selectedBaselineId, setSelectedBaselineId] = useState<string | null>(null);
  const projectsFetchInFlight = useRef(false);
  const projectsAuthRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectsAuthRetried = useRef(false);
  const lastTrackedProjectId = useRef<string | null>(null);
  const lastTrackedSnapshotId = useRef<string | null>(null);
  const lastTrackedBaselineId = useRef<string | null>(null);

  const fetchProjects = async (preferProjectId?: string) => {
    if (projectsFetchInFlight.current) return;
    projectsFetchInFlight.current = true;
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      
      const endpoint = `${API_BASE_URL}/projects`;
      const headers = await getAuthHeaders();
      if (import.meta.env.DEV) console.log('🔍 GlobalProjectContext: Fetching projects from:', endpoint);
      
      const response = await authFetch(endpoint, { headers, credentials: 'include', noRedirect: true });
      if (!response.ok) {
        if (response.status === 401) {
          setProjects([]);
          if (!projectsAuthRetried.current && !projectsAuthRetryTimer.current) {
            projectsAuthRetried.current = true;
            projectsAuthRetryTimer.current = setTimeout(() => {
              projectsAuthRetryTimer.current = null;
              fetchProjects(preferProjectId);
            }, 2500);
          }
          return;
        }
        throw new Error(`Failed to fetch projects: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      const rawList = Array.isArray(data) ? data : (data?.items ?? data?.projects ?? data?.data ?? []);
      const arr = Array.isArray(rawList) ? rawList : [];
      if (import.meta.env.DEV) console.log('🔍 GlobalProjectContext: Projects fetched successfully:', arr.length, 'projects');
      const decoded = arr.map((p: Project) => ({
        ...p,
        name: decodeUnicodeEscapes(p.name ?? p.display_name ?? ''),
        display_name: p.display_name ? decodeUnicodeEscapes(p.display_name) : p.display_name,
      }));
      setProjects(decoded);

      // Restore selection: API prefer id > in-session selection > URL project > localStorage > safe default.
      if (decoded.length > 0) {
        const storedProjectId =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem(CURRENT_PROJECT_STORAGE_KEY)
            : null;
        const path =
          typeof window !== 'undefined' ? window.location.pathname : '';
        const routeProjectId = routeProjectIdFromPath(path);
        const preferFromParam =
          preferProjectId && decoded.some((p: Project) => p.id === preferProjectId)
            ? preferProjectId
            : null;
        const preferCurrent =
          selectedProject?.id && decoded.some((p: Project) => p.id === selectedProject.id)
            ? selectedProject.id
            : null;
        const preferRoute =
          routeProjectId && decoded.some((p: Project) => p.id === routeProjectId)
            ? routeProjectId
            : null;
        const preferStored =
          storedProjectId && decoded.some((p: Project) => p.id === storedProjectId)
            ? storedProjectId
            : null;

        const preferredProjectId =
          preferFromParam ?? preferCurrent ?? preferRoute ?? preferStored ?? null;

        let projectToSelect: Project | null = preferredProjectId
          ? decoded.find((p: Project) => p.id === preferredProjectId) ?? null
          : null;

        if (!projectToSelect) {
          if (storedProjectId && !preferStored && typeof localStorage !== 'undefined') {
            localStorage.removeItem(CURRENT_PROJECT_STORAGE_KEY);
          }
          const hadUserPreference =
            Boolean(storedProjectId) || Boolean(preferFromParam) || Boolean(preferCurrent);
          const coldFirstVisit = !hadUserPreference && !preferRoute;
          projectToSelect = coldFirstVisit
            ? decoded.find((p: Project) => p.has_xer_data) ?? decoded[0]
            : decoded[0];
        }

        if (!selectedProject || selectedProject.id !== projectToSelect.id) {
          if (import.meta.env.DEV)
            console.log(
              '🔍 GlobalProjectContext: Selecting project:',
              projectToSelect.name || projectToSelect.display_name
            );
          setSelectedProject(projectToSelect);
        }
      }
      
    } catch (err) {
      if (import.meta.env.DEV) console.error('❌ GlobalProjectContext: Error fetching projects:', err);
      setProjectsError(err instanceof Error ? err.message : 'Failed to load projects');
      setProjects([]);
    } finally {
      setProjectsLoading(false);
      projectsFetchInFlight.current = false;
    }
  };

  const refreshProjects = async (preferProjectId?: string) => {
    await fetchProjects(preferProjectId);
  };

  const selectProjectById = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSelectedProject(project);
    } else {
      if (import.meta.env.DEV) console.warn('⚠️ GlobalProjectContext: Project not found with ID:', projectId);
    }
  };

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (selectedProject?.id) {
      localStorage.setItem(CURRENT_PROJECT_STORAGE_KEY, selectedProject.id);
    }
  }, [selectedProject?.id]);

  // Single fetch for both snapshots and baselines — the same /v1/snapshots/{id} endpoint
  // returns all data needed for both. Previously two concurrent calls were made to the same
  // URL, doubling the request count every time a project was selected.
  const fetchSnapshotsAndBaselines = async (projectId: string) => {
    setSnapshotsLoading(true);
    setBaselinesLoading(true);
    setSelectedSnapshotId(null);
    setSelectedBaselineId(null);
    try {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE_URL}/v1/snapshots/${projectId}`, { headers, credentials: 'include', noRedirect: true });
      if (!response.ok) {
        setSnapshots([]);
        setBaselines([]);
        return;
      }
      const data = await response.json();
      const allSnapshots: Array<{
        id: string;
        snapshot_name?: string;
        file_name?: string;
        uploaded_at?: string | null;
        is_baseline?: boolean;
        is_latest?: boolean;
        import_run_id?: string;
        snapshot_type?: string;
        metadata?: { total_activities?: number | null };
      }> = data.snapshots ?? [];

      // Snapshots list (all snapshots for context selector)
      const list = allSnapshots.map((s) => ({
        id: s.id,
        snapshot_name: decodeUnicodeEscapes(s.snapshot_name ?? 'Unnamed'),
        uploaded_at: s.uploaded_at ?? null,
        is_baseline: s.is_baseline,
        is_latest: s.is_latest,
        import_run_id: s.import_run_id,
        total_activities: Number(s?.metadata?.total_activities ?? 0),
      }));
      setSnapshots(list);
      if (list.length > 0) {
        const latestSchedule =
          list.find((s) => !s.is_baseline && (s.total_activities ?? 0) > 0) ??
          list.find((s) => !s.is_baseline) ??
          list.find((s) => s.is_latest) ??
          list[0];
        setSelectedSnapshotId(latestSchedule.id);
      }

      // Baselines list (snapshots flagged as baselines, for baseline selector)
      const baselineList = allSnapshots.filter(
        (s) => s.is_baseline === true || s.snapshot_type === 'baseline'
      );
      const mapped = baselineList.map((s) => ({
        id: s.id,
        snapshot_name: decodeUnicodeEscapes(s.snapshot_name ?? ''),
        file_name: decodeUnicodeEscapes(s.file_name ?? ''),
        uploaded_at: s.uploaded_at ?? undefined,
        is_baseline: s.is_baseline,
      }));
      setBaselines(mapped);
      const active = mapped.find((b) => b.is_baseline) ?? null;
      setSelectedBaselineId(active ? active.id : null);
    } catch {
      setSnapshots([]);
      setBaselines([]);
    } finally {
      setSnapshotsLoading(false);
      setBaselinesLoading(false);
    }
  };

  // Keep individual refresh helpers for callers that only want one; both now reuse the shared fetch.
  const refreshSnapshots = async () => {
    if (selectedProject?.id) {
      await fetchSnapshotsAndBaselines(selectedProject.id);
    } else {
      setSnapshots([]);
      setSelectedSnapshotId(null);
    }
  };

  const refreshBaselines = async () => {
    if (selectedProject?.id) {
      await fetchSnapshotsAndBaselines(selectedProject.id);
    } else {
      setBaselines([]);
      setSelectedBaselineId(null);
    }
  };

  const setSelectedBaselineIdWithPersistence = async (id: string | null) => {
    const projectId = selectedProject?.id;
    if (!projectId) {
      setSelectedBaselineId(null);
      return;
    }
    const headers = await getAuthHeaders();
    try {
      if (id) {
        const res = await authFetch(`${API_BASE_URL}/v1/snapshots/${id}/set-baseline`, {
          method: 'POST',
          headers,
          credentials: 'include',
          noRedirect: true,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail ?? 'Failed to set baseline');
        }
      } else {
        const currentId = selectedBaselineId;
        if (currentId) {
          const res = await authFetch(`${API_BASE_URL}/v1/snapshots/${currentId}/unset-baseline`, {
            method: 'POST',
            headers,
            credentials: 'include',
            noRedirect: true,
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail ?? 'Failed to clear baseline');
          }
        }
      }
      await refreshBaselines();
    } catch (e) {
      if (import.meta.env.DEV) console.error('Baseline update failed:', e);
      await refreshBaselines();
    }
  };

  // Initial load: only fetch projects when authenticated (avoids 401 before session is ready)
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setProjectsLoading(false);
      setProjects([]);
      setProjectsError(null);
      return;
    }
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run when auth becomes true
  }, [isAuthenticated, authLoading]);

  // When selected project changes, fetch its snapshots (context) and baselines
  useEffect(() => {
    if (selectedProject?.id) {
      // Single call fetches both snapshots and baselines from the same endpoint (halves requests).
      fetchSnapshotsAndBaselines(selectedProject.id);
    } else {
      setSnapshots([]);
      setSelectedSnapshotId(null);
      setBaselines([]);
      setSelectedBaselineId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSnapshotsAndBaselines is stable; selectedProject.id is key
  }, [selectedProject?.id]);

  // Debug logging for selected project changes (dev only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      if (selectedProject) {
        console.log('🔍 GlobalProjectContext: Selected project changed to:', selectedProject.name);
      } else {
        console.log('🔍 GlobalProjectContext: No project selected');
      }
    }
  }, [selectedProject]);

  // PostHog: track project selection (skip initial load / auto-select)
  useEffect(() => {
    if (!selectedProject?.id || !posthog) return;
    if (lastTrackedProjectId.current === selectedProject.id) return;
    lastTrackedProjectId.current = selectedProject.id;
    posthog.capture('project_selected', {
      project_id: selectedProject.id,
      project_name: selectedProject.name ?? undefined,
    });
  }, [selectedProject?.id, selectedProject?.name, posthog]);

  // PostHog: track snapshot selection when user changes it (only when a snapshot is selected)
  useEffect(() => {
    if (!posthog || !selectedSnapshotId) return;
    if (lastTrackedSnapshotId.current === selectedSnapshotId) return;
    lastTrackedSnapshotId.current = selectedSnapshotId;
    posthog.capture('snapshot_selected', {
      project_id: selectedProject?.id ?? undefined,
      snapshot_id: selectedSnapshotId,
    });
  }, [selectedSnapshotId, selectedProject?.id, posthog]);

  // PostHog: track baseline selection when user changes it (only when a baseline is selected)
  useEffect(() => {
    if (!posthog || selectedBaselineId == null) return;
    if (lastTrackedBaselineId.current === selectedBaselineId) return;
    lastTrackedBaselineId.current = selectedBaselineId;
    posthog.capture('baseline_selected', {
      project_id: selectedProject?.id ?? undefined,
      baseline_id: selectedBaselineId,
    });
  }, [selectedBaselineId, selectedProject?.id, posthog]);

  const value: GlobalProjectContextType = {
    selectedProject,
    projects,
    setSelectedProject,
    snapshots,
    snapshotsLoading,
    selectedSnapshotId,
    setSelectedSnapshotId,
    refreshSnapshots,
    baselines,
    baselinesLoading,
    selectedBaselineId,
    setSelectedBaselineId: setSelectedBaselineIdWithPersistence,
    refreshBaselines,
    projectsLoading,
    projectsError,
    refreshProjects,
    selectProjectById,
  };

  return (
    <GlobalProjectContext.Provider value={value}>
      {children}
    </GlobalProjectContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- useGlobalProject is hook, GlobalProjectProvider is component
export const useGlobalProject = (): GlobalProjectContextType => {
  const context = useContext(GlobalProjectContext);
  if (context === undefined) {
    throw new Error('useGlobalProject must be used within a GlobalProjectProvider');
  }
  return context;
};

export default GlobalProjectContext;
